import React, { useCallback, useEffect, useState } from 'react';
import store from '@/state/store';
import { ButtonProps } from '@mui/material';
import { AccessLevel } from '@bimdata/definitions/const';
import { conceal, share } from '@/api/project';
import projectSlice from '@/state/project.slice';
import getShareCode from '@/loaders/project/share-code.loader';
import PanelButton from '@/components/PanelButton';
import ShareIcon from '@/components/icons/Share';
import ProjectShareDialog from './ProjectShareDialog';
import { RootState, useAppSelector } from '@/state/store';
import { createSelector } from '@reduxjs/toolkit';
import { ProjectData } from '@/state/types/project-data';
import hinted from '@/components/Hinted';
import t from '@/utils/translate';

export type ShareButtonProps = {
  project: ProjectData;
} & Omit<ButtonProps, 'loading'>;

const OpenButton = hinted(PanelButton, {});

export default ({ project, ...props }: ShareButtonProps) => {
  const { shareCode } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.projects.shareCodes[project.code]
      ],
      (shareCode) => ({ shareCode })
    )
  );

  const [shareDialog, setShareDialog] = useState<boolean>(false);

  const shareProject = useCallback(async () => {
    const code = await share(project.code);
    store.dispatch(projectSlice.actions.setShareCode({ project: project.code, code }));
  }, [ project.code ]);

  const concealProject = useCallback(async () => {
    await conceal(project.code);
    store.dispatch(projectSlice.actions.setShareCode({ project: project.code, code: null }));
  }, [ project.code ]);
  
  useEffect(() => {
    if ((typeof shareCode === 'undefined') && (project.access === AccessLevel.OWNER)) {
      getShareCode(project.code);
    }
  }, [project.code, project.access, shareCode]);

  return (typeof shareCode !== 'undefined') ? (
    <>
      <OpenButton
        {...props}
        hint={t('project.action.share')}
        colors={
          shareCode
          ? {
            default: '#0065FF',
            hovered: '#DE350B'
          }
          : {
            hovered: '#0065FF'
          }
        }
        onClick={() => setShareDialog(true)}
      >
        <ShareIcon size={24} />
      </OpenButton>
      <ProjectShareDialog
        shareCode={shareCode}
        open={shareDialog}
        onClose={() => setShareDialog(false)}
        share={shareProject}
        conceal={concealProject}
      />
    </>
  ) : (<></>);
}